import {
  BookOpenText,
  Bookmark,
  BrainCircuit,
  ClipboardCheck,
  Cloud,
  Code2,
  Database,
  Home,
  LogOut,
  Megaphone,
  Menu,
  MessageCircleQuestion,
  Moon,
  Palette,
  Rocket,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Sun,
  Triangle
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { isAdminRole, useAuthStore } from "../../features/auth/authStore";
import { cn } from "../../lib/cn";
import { THEME_KEY } from "../../lib/constants";
import { GenieLampLogo } from "../common/GenieLampLogo";
import { Button } from "../ui/button";

type NavCategory = "development" | "quality" | "launch" | "library";

const categoryItems: Array<{ id: NavCategory; label: string; description: string; icon: typeof Code2; menuPosition: string }> = [
  { id: "development", label: "開発", description: "アイデア探しと要件整理", icon: Code2, menuPosition: "left-0" },
  { id: "quality", label: "品質", description: "診断とUI改善", icon: ShieldCheck, menuPosition: "left-1/4 -translate-x-1/4" },
  { id: "launch", label: "公開準備", description: "デプロイと集客", icon: Rocket, menuPosition: "left-2/4 -translate-x-1/4" },
  { id: "library", label: "管理", description: "保存と設定", icon: Bookmark, menuPosition: "right-0" }
];

const navItems: Array<{ to: string; label: string; category: NavCategory; icon: typeof Home; description: string }> = [
  { to: "/home", label: "ホーム", category: "development", icon: Home, description: "おすすめアイデアと最近の活動を確認" },
  { to: "/questions", label: "質問", category: "development", icon: MessageCircleQuestion, description: "質問に答えて作るアイデアを見つける" },
  { to: "/deep-dive", label: "深掘り", category: "development", icon: BrainCircuit, description: "AIと対話してMVP案を1つに絞る" },
  { to: "/swipe", label: "スワイプ", category: "development", icon: Sparkles, description: "条件をスワイプして候補を絞り込む" },
  { to: "/design", label: "デザイン", category: "quality", icon: Palette, description: "ZIPからUI改善プロンプトを生成" },
  { to: "/diagnosis", label: "診断", category: "quality", icon: Stethoscope, description: "ZIPをアップロードして公開前チェック" },
  { to: "/marketing", label: "マーケ", category: "launch", icon: Megaphone, description: "計測、SEO、広告準備の実装プロンプトを生成" },
  { to: "/pre-launch", label: "ローンチ前", category: "launch", icon: ClipboardCheck, description: "公開、集客、検証、収益化の成果物を生成" },
  { to: "/vibe-deploy", label: "デプロイ", category: "launch", icon: Rocket, description: "ZIPから公開準備の修正プロンプトを生成" },
  { to: "/saved", label: "保存", category: "library", icon: Bookmark, description: "保存したアイデアを見返す" },
  { to: "/settings", label: "設定", category: "library", icon: Settings, description: "プロフィールとAI設定を変更" }
];

const adminNavItems = [
  { to: "/admin/samples", label: "データ", subLabel: "サンプル投入", icon: Database, description: "初期アイデアデータを同期" },
  { to: "/admin/settings", label: "プロフィール設定", subLabel: "選択肢とAIモデル", icon: Settings, description: "プロフィール選択肢を変更" },
  { to: "/admin/research", label: "調査", subLabel: "操作記録", icon: Search, description: "ユーザー別の保存と生成履歴を確認" },
  { to: "/admin/cost", label: "運用", subLabel: "コスト追跡", icon: ShieldCheck, description: "AI利用量と推定コストを確認" }
];

export function Header({ onMenu }: { onMenu: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef<HTMLElement | null>(null);
  const [dark, setDark] = useState(false);
  const [openCategory, setOpenCategory] = useState<NavCategory | null>(null);
  const role = useAuthStore((state) => state.role);
  const logout = useAuthStore((state) => state.logout);
  const isAdmin = isAdminRole(role);
  const activeItem = navItems.find((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  const activeCategory = activeItem?.category ?? "development";
  const openCategoryMeta = categoryItems.find((category) => category.id === openCategory);
  const visibleNavItems = isAdmin ? adminNavItems : navItems.filter((item) => item.category === openCategory);

  useEffect(() => {
    setDark(document.documentElement.classList.contains("dark"));
  }, []);

  useEffect(() => {
    setOpenCategory(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openCategory) return;

    function closeOnOutsidePointerDown(event: PointerEvent) {
      if (!headerRef.current?.contains(event.target as Node)) {
        setOpenCategory(null);
      }
    }

    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, [openCategory]);

  function toggleTheme() {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem(THEME_KEY, next ? "dark" : "light");
  }

  return (
    <header ref={headerRef} className="sticky top-0 z-30 border-b border-[color:var(--border)] bg-[color:var(--surface)]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-card text-[color:var(--muted)] hover:bg-[color:var(--surface-raised)] lg:hidden"
            onClick={onMenu}
            aria-label="ナビゲーションを開く"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>
          <Link to={isAdmin ? "/admin/cost" : "/home"} className="flex min-w-0 items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center">
              <GenieLampLogo className="h-full w-full" />
            </span>
            <span className="grid min-w-0 gap-0.5 max-[380px]:hidden">
              <span className="font-display text-lg font-bold leading-none tracking-normal text-[color:var(--text)]">VibeGenie</span>
              <span className="hidden truncate text-xs font-medium leading-none text-[color:var(--muted)] sm:block">
                {isAdmin ? "管理コンソール" : "今日、何作る？を見つける"}
              </span>
            </span>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!isAdmin ? (
            <>
              <UtilityLink to="/glossary" label="用語集" icon={BookOpenText} ariaLabel="VibeCoder向け開発用語集を開く" />
              <UtilityLink to="/cloudflare" label="Cloudflare" icon={Cloud} ariaLabel="VibeCoder向けCloudflareセットアップマニュアルを開く" />
              <UtilityLink to="/vercel" label="Vercel" icon={Triangle} ariaLabel="VibeCoder向けVercelセットアップマニュアルを開く" />
            </>
          ) : null}
          <UtilityLink
            to={isAdmin ? "/admin/settings" : "/settings"}
            label="設定"
            icon={Settings}
            ariaLabel={isAdmin ? "管理者向け設定を開く" : "プロフィールとAI設定を開く"}
          />
          <Button className="px-2 sm:px-4" variant="ghost" onClick={toggleTheme} aria-label={dark ? "ライトモードに切り替え" : "ダークモードに切り替え"}>
            {dark ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
            <span className="hidden sm:inline">{dark ? "Light" : "Dark"}</span>
          </Button>
          <Button
            variant="ghost"
            onClick={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
            aria-label="ログアウト"
            className="px-2 sm:px-4"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">ログアウト</span>
          </Button>
        </div>
      </div>

      {!isAdmin ? (
        <nav className="hidden border-t border-[color:var(--border)] px-4 py-2 md:px-6 lg:block" aria-label="用途カテゴリ">
          <div className="mx-auto grid max-w-7xl grid-cols-4 gap-2">
            {categoryItems.map((category) => {
              const Icon = category.icon;
              const active = category.id === activeCategory;
              const expanded = category.id === openCategory;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setOpenCategory((current) => (current === category.id ? null : category.id))}
                  className={cn(
                    "flex min-h-12 items-center gap-3 rounded-card px-4 py-2 text-left transition",
                    expanded
                      ? "bg-[color:var(--surface-warm)] text-[color:var(--text)] shadow-[0_1px_0_color-mix(in_oklch,var(--surface),white_35%)_inset]"
                      : active
                        ? "bg-[color:var(--surface-raised)] text-[color:var(--text)]"
                        : "text-[color:var(--muted)] hover:bg-[color:var(--surface-raised)] hover:text-[color:var(--text)]"
                  )}
                  aria-expanded={expanded}
                  aria-controls="header-feature-menu"
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold leading-none">{category.label}</span>
                    <span className="mt-1 block truncate text-[0.68rem] font-semibold leading-none text-[color:var(--muted)]">{category.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      ) : null}

      {(isAdmin || openCategory) && visibleNavItems.length ? (
        <nav
          id="header-feature-menu"
          className={cn(
            "hidden px-4 md:px-6 lg:block",
            isAdmin
              ? "border-t border-[color:var(--border)] bg-[color:var(--surface)]/96 py-3"
              : "absolute inset-x-0 top-full pointer-events-none py-2"
          )}
          aria-label="主要機能ナビゲーション"
        >
          <div
            className={cn(
              "mx-auto max-w-7xl",
              isAdmin
                ? "grid grid-cols-4 gap-2"
                : "relative min-h-0"
            )}
          >
            <div
              className={cn(
                isAdmin
                  ? "contents"
                  : "pointer-events-auto absolute top-0 grid w-[26rem] max-w-[calc(100vw-2rem)] gap-1 rounded-card border border-[color:var(--border)] bg-[color:var(--surface)] p-2 shadow-soft",
                !isAdmin && openCategoryMeta?.menuPosition
              )}
            >
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpenCategory(null)}
                    aria-label={`${item.label}: ${item.description}`}
                    className={({ isActive }) =>
                      cn(
                        "group relative inline-flex min-h-12 items-center gap-3 rounded-card px-4 py-3 text-left text-sm font-bold transition",
                        isActive
                          ? "bg-[color:var(--surface-warm)] text-[color:var(--text)] shadow-[0_1px_0_color-mix(in_oklch,var(--surface),white_35%)_inset]"
                          : "text-[color:var(--muted)] hover:bg-[color:var(--surface-raised)] hover:text-[color:var(--text)]"
                      )
                    }
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-card bg-[color:var(--surface-raised)] text-[color:var(--primary)]">
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="grid min-w-0 gap-1">
                      <span className="truncate text-sm font-bold leading-none text-[color:var(--text)]">{item.label}</span>
                      <span className="truncate text-xs font-semibold leading-none text-[color:var(--muted)]">{item.description}</span>
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </nav>
      ) : null}
    </header>
  );
}

function UtilityLink({ to, label, ariaLabel, icon: Icon }: { to: string; label: string; ariaLabel: string; icon: typeof BookOpenText }) {
  return (
    <NavLink
      to={to}
      aria-label={ariaLabel}
      className={({ isActive }) =>
        cn(
          "inline-flex min-h-11 items-center justify-center gap-2 rounded-card px-2 py-2 text-sm font-bold transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:px-3",
          isActive
            ? "bg-[color:var(--surface-raised)] text-[color:var(--text)]"
            : "text-[color:var(--muted)] hover:bg-[color:var(--surface-raised)] hover:text-[color:var(--text)]"
        )
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
      <span className="hidden sm:inline">{label}</span>
    </NavLink>
  );
}
